import { useEffect, useState } from 'react';

/**
 * useAnchoredRect — THE way a pad panel anchors to the field it belongs to.
 *
 * The prescription pad is `overflow: hidden` and is mounted in a dashboard
 * column, a modal and a narrow accordion, so an `absolute` panel inside a
 * section gets clipped (and, in the two-column layout, takes its origin from
 * the COLUMN rather than from its own input — which is what put the Tests
 * field's template panel off to the left, cut off, on the owner's live run).
 *
 * The fix the medicine popover already shipped (T2a) is: measure the input's
 * own rect and paint the panel `position: fixed` at it. A fixed box's
 * containing block is the viewport, so no ancestor's `overflow` can clip it and
 * no column can shift it — and it is re-measured while open, because scrolling
 * an ancestor or resizing the window moves the input under it.
 *
 * This module is that measurement, ONCE, for every field that needs it
 * (medicine box, diagnosis, symptom picker, lab-test templates). Nothing here
 * fetches, and it adds exactly two passive listeners while a panel is open.
 *
 * @param {boolean} open  is the panel showing? (closed = no listeners at all)
 * @param {{current: HTMLElement|null}} ref  the ANCHOR (the field's own input)
 * @returns {{top: number, left: number, width: number, anchorTop: number}|null}
 *          `top` is the anchor's BOTTOM (where a panel under it starts);
 *          `anchorTop` is kept so a panel can flip above it.
 */
export default function useAnchoredRect(open, ref) {
  const [rect, setRect] = useState(null);

  useEffect(() => {
    if (!open) return undefined;
    const measure = () => {
      const el = ref.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      setRect((prev) => (
        prev && prev.top === r.bottom && prev.left === r.left
          && prev.width === r.width && prev.anchorTop === r.top
          ? prev
          : { top: r.bottom, left: r.left, width: r.width, anchorTop: r.top }
      ));
    };
    measure();
    // `true` — a scroll on ANY ancestor moves the anchor, not just the window.
    window.addEventListener('scroll', measure, true);
    window.addEventListener('resize', measure);
    return () => {
      window.removeEventListener('scroll', measure, true);
      window.removeEventListener('resize', measure);
    };
  }, [open, ref]);

  return rect;
}

/**
 * Keep a panel of `size` inside the viewport, given its anchor's rect.
 *
 * NO ANCHOR → **null**, never a placement (bk-986). A `{0,0}` fallback reads
 * as a real coordinate, so a `position: fixed` panel painted at it lands on the
 * viewport's top-left corner — which is exactly what the Tests panel did for an
 * organisation with no lab catalogue (no search box → no anchor → no rect), and
 * what every panel's FIRST frame did before the measuring effect ran. A caller
 * that gets null renders IN FLOW instead.
 */
export function clampToViewport(rect, { width, height = 0, margin = 8 } = {}) {
  if (!rect) return null;
  const vw = window.innerWidth || 1024;
  const vh = window.innerHeight || 768;
  const w = width || rect.width || 0;

  // shift left when the field sits near the right edge…
  let left = Math.min(rect.left, vw - w - margin);
  left = Math.max(margin, left);

  // …and flip above the field when there is no room under it.
  let top = rect.top;
  if (height > 0 && top + height > vh - margin) {
    const above = (rect.anchorTop ?? rect.top) - height - 6;
    top = above >= margin ? above : Math.max(margin, vh - height - margin);
  }
  return { top, left };
}
